import { SITE_ID } from '../assets/meta/meta';

export const VILLAGE_SYSTEM_NOTICE_VERSION = '2026-08-04';
export const VILLAGE_SYSTEM_NOTICE_CHANGELOG_DATE = '2026-08-04';
export const VILLAGE_SYSTEM_NOTICE_SITE_IDS = [32, 339, 369, 435] as const;

const VILLAGE_SYSTEM_NOTICE_STORAGE_PREFIX = 'amazing_crm_village_system_notice';
const VILLAGE_SYSTEM_NOTICE_SITE_ID_SET = new Set<number>(VILLAGE_SYSTEM_NOTICE_SITE_IDS);

type NoticeStorage = Pick<Storage, 'getItem' | 'setItem'>;

interface VillageSystemNoticeContext {
  siteId?: number;
  userId?: number | null;
  storage?: NoticeStorage | null;
}

interface DashboardChangelogSuppressionContext extends VillageSystemNoticeContext {
  latestChangelogDate?: string | null;
}

const getDefaultStorage = (): NoticeStorage | null => {
  if (typeof window === 'undefined') return null;

  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

const getNoticeStorageKey = (siteId: number, userId: number) => (
  `${VILLAGE_SYSTEM_NOTICE_STORAGE_PREFIX}:${siteId}:${userId}`
);

export function isVillageSystemNoticeTarget(siteId = SITE_ID): boolean {
  return VILLAGE_SYSTEM_NOTICE_SITE_ID_SET.has(Number(siteId));
}

export function shouldShowVillageSystemNotice({
  siteId = SITE_ID,
  userId,
  storage = getDefaultStorage(),
}: VillageSystemNoticeContext = {}): boolean {
  const normalizedSiteId = Number(siteId);
  const normalizedUserId = Number(userId);

  if (!isVillageSystemNoticeTarget(normalizedSiteId)) return false;
  if (!Number.isInteger(normalizedUserId) || !storage) return true;

  try {
    return storage.getItem(getNoticeStorageKey(normalizedSiteId, normalizedUserId)) !== VILLAGE_SYSTEM_NOTICE_VERSION;
  } catch {
    return true;
  }
}

export function dismissVillageSystemNotice({
  siteId = SITE_ID,
  userId,
  storage = getDefaultStorage(),
}: VillageSystemNoticeContext = {}): void {
  const normalizedSiteId = Number(siteId);
  const normalizedUserId = Number(userId);

  if (!isVillageSystemNoticeTarget(normalizedSiteId)) return;
  if (!Number.isInteger(normalizedUserId) || !storage) return;

  try {
    storage.setItem(
      getNoticeStorageKey(normalizedSiteId, normalizedUserId),
      VILLAGE_SYSTEM_NOTICE_VERSION,
    );
  } catch {
    // The notice can still close when browser storage is unavailable.
  }
}

export function shouldSuppressDashboardChangelogAutoOpen({
  siteId = SITE_ID,
  userId,
  latestChangelogDate,
  storage = getDefaultStorage(),
}: DashboardChangelogSuppressionContext = {}): boolean {
  if (!isVillageSystemNoticeTarget(siteId)) return false;

  if (latestChangelogDate === VILLAGE_SYSTEM_NOTICE_CHANGELOG_DATE) {
    return true;
  }

  return shouldShowVillageSystemNotice({ siteId, userId, storage });
}
